import type * as THREE from 'three';
import type { FloraDefinition } from './FloraDefinition';
import type { FloraInstance } from './FloraInstance';
import type { GrowthState } from './FloraGrowthModel';
import type { FloraGeometryDetail } from './FloraGeometry';

/**
 * The seam between procedural placeholder plants and authored 3D models.
 *
 * `FloraFactory` asks the registry for a provider before falling back to
 * {@link buildFloraGeometry}. Registering a provider for a definition id swaps that species over to
 * an authored asset without touching the editor or the season model.
 *
 * Providers are expected to honour the {@link FloraMeshRole} naming convention so the rest of the
 * pipeline keeps working:
 *
 * - a mesh named `foliage` is recoloured continuously by the season model and hidden when dormant;
 * - a mesh named `accent` is shown only inside a bloom/fruit window and takes the blossom colour;
 * - anything else is treated as woody structure and left alone.
 *
 * Providers must also honour {@link FloraModelRequest.growth}: maturity is *non-uniform* (spec §8.3),
 * so a provider that merely uniform-scales a static mesh loses fidelity the procedural path has. Use
 * `growth.heightMeters` / `growth.spreadMeters` as separate axes at minimum.
 */

export const FLORA_MESH_ROLES = ['structure', 'foliage', 'accent'] as const;
export type FloraMeshRole = (typeof FLORA_MESH_ROLES)[number];

/** Classify a scene node by the naming convention above. */
export function meshRole(name: string): FloraMeshRole {
  const lower = name.toLowerCase();
  if (lower.includes('foliage') || lower.includes('leaf') || lower.includes('leaves')) return 'foliage';
  if (lower.includes('accent') || lower.includes('flower') || lower.includes('blossom')) return 'accent';
  return 'structure';
}

export interface FloraModelRequest {
  definition: FloraDefinition;
  instance: FloraInstance;
  /** Resolved non-uniform size at the instance's maturity, including its biological modifiers. */
  growth: GrowthState;
  detail: FloraGeometryDetail;
}

export interface FloraModelProvider {
  /** Stable identifier, surfaced in the inspector so a designer can see which asset is in use. */
  readonly id: string;
  /**
   * Build a view for this request. Returning `null` defers to the procedural generator, which lets a
   * provider handle only the maturities or detail tiers it has assets for.
   */
  build(request: FloraModelRequest): THREE.Object3D | null;
}

/** Maps flora definition ids to authored-model providers. Empty by default — everything procedural. */
export class FloraModelRegistry {
  private readonly byDefinition = new Map<string, FloraModelProvider>();

  register(definitionId: string, provider: FloraModelProvider): void {
    this.byDefinition.set(definitionId, provider);
  }

  unregister(definitionId: string): boolean {
    return this.byDefinition.delete(definitionId);
  }

  has(definitionId: string): boolean {
    return this.byDefinition.has(definitionId);
  }

  resolve(definitionId: string): FloraModelProvider | undefined {
    return this.byDefinition.get(definitionId);
  }

  /** Definition ids currently backed by an authored model. */
  registeredIds(): string[] {
    return [...this.byDefinition.keys()];
  }

  clear(): void {
    this.byDefinition.clear();
  }
}

/**
 * The registry the application uses. Import this and call `register` once your models are loaded —
 * newly built plant views pick them up immediately, and existing ones on the next rebuild.
 */
export const floraModels = new FloraModelRegistry();
